/**
 * The ONLY file permitted to import `polygon-clipping` (SD-4.2, AD-26). Everything else goes
 * through it; `dependencies.test.ts` greps the tree to keep that true.
 *
 * The library is used for exactly one operation, the one the FR-36 spike proved we need and
 * cannot write in an afternoon: resolving a self-intersecting ring into simple regions. Per-edge
 * offsetting creates such rings at reflex vertices; a keep-out drawn as a bowtie IS one. No
 * boolean library does offsetting itself — `polygon-clipping` exposes union/intersection/xor/
 * difference only, and `clipper-lib`'s uniform delta cannot express FR-10 — so offsetting lives
 * in `offset.ts` as our own code and only the resolution step lands here.
 *
 * SD-4.6 added the second operation, `difference`, for subtracting keep-outs from a placeable
 * region — named by that story's acceptance criteria and routed through here for the same reason.
 *
 * ⚠️ THE TWO FUNCTIONS TREAT HOLES OPPOSITELY, AND BOTH ARE CORRECT. `resolveSelfIntersections`
 * DROPS holes, because its input is a keep-out ring and dropping a hole there BLOCKS MORE — the
 * conservative direction. `subtractRegions` PRESERVES them, because there a hole IS the keep-out:
 * a skylight in the middle of a face produces exactly one, and dropping it puts panels back over
 * the skylight. Same word, opposite consequence.
 */

import polygonClipping from 'polygon-clipping';

import { signedArea, toCCW } from './predicates';
import type { Pt } from './types';

/** Below this (1 mm²) a region is a floating-point sliver, not a roof. */
export const MIN_REGION_AREA_M2 = 1e-6;

/**
 * Resolve a possibly self-intersecting ring into simple CCW regions via boolean union.
 *
 * Degenerate inputs (fewer than 3 points, non-finite coordinates, zero area) return `[]` —
 * degenerate outputs are legal, not errors. Winding of the input is irrelevant; callers that
 * care which lobes are genuinely interior (offsetting does) must filter by winding themselves,
 * because the union normalises winding and cannot see the difference.
 *
 * Holes in the union result are deliberately dropped: a single input ring only produces one in
 * pathological double-wrap cases, and for keep-outs dropping the hole errs conservative (blocks
 * more, never less).
 */
export function resolveSelfIntersections(ring: readonly Pt[]): Pt[][] {
  if (!ring || ring.length < 3) return [];
  if (ring.some((p) => !Number.isFinite(p?.[0]) || !Number.isFinite(p?.[1]))) return [];

  let resolved: ReturnType<typeof polygonClipping.union>;
  try {
    const closed = [...ring.map((p): Pt => [p[0], p[1]]), [ring[0][0], ring[0][1]] as Pt];
    resolved = polygonClipping.union([closed]);
  } catch {
    // The library throws on some pathological inputs; a degenerate output is the contract.
    return [];
  }

  const regions: Pt[][] = [];
  for (const poly of resolved) {
    const outer = poly[0];
    if (!outer || outer.length < 4) continue;
    // Output rings are closed (first vertex repeated); our rings are open.
    const open = outer.slice(0, -1).map((p): Pt => [p[0], p[1]]);
    if (open.length >= 3 && Math.abs(signedArea(open)) > MIN_REGION_AREA_M2) {
      regions.push(toCCW(open));
    }
  }
  return regions;
}


/**
 * A placeable area: an outer boundary with the keep-outs inside it as holes.
 *
 * A rect is placeable when it fits inside `outer` and touches no `hole`. The two are kept apart
 * rather than flattened into one ring list because they answer different questions — containment
 * versus exclusion — and because the packer's GRID ANCHOR is derived from the un-subtracted region,
 * never from this (see `pack.ts`).
 */
export interface PlaceableRegion {
  outer: Pt[];
  holes: Pt[][];
}

/**
 * `regions − subtrahends`, via `polygonClipping.difference`.
 *
 * Every subtrahend is first passed through `resolveSelfIntersections`, so a user-drawn bowtie has
 * BOTH its lobes subtracted rather than whichever the library happened to consider interior — the
 * same normalisation `packPanels` already applied to its keep-outs.
 *
 * Degenerate inputs are degenerate outputs, not errors: a region under 3 vertices contributes
 * nothing, a malformed subtrahend subtracts nothing, and a subtrahend covering everything returns
 * an empty list. Slivers below `MIN_REGION_AREA_M2` are discarded on the same threshold the union
 * uses, so a keep-out that leaves a tenth of a millimetre does not produce a "region".
 */
export function subtractRegions(
  regions: readonly (readonly Pt[])[],
  subtrahends: readonly (readonly Pt[])[]
): PlaceableRegion[] {
  const usableRegions = regions.filter(
    (r) => r && r.length >= 3 && r.every((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]))
  );
  if (usableRegions.length === 0) return [];

  const cutters = subtrahends.flatMap((s) => resolveSelfIntersections(s));
  if (cutters.length === 0) {
    return usableRegions
      .filter((r) => Math.abs(signedArea(r)) > MIN_REGION_AREA_M2)
      .map((r) => ({ outer: toCCW(r), holes: [] }));
  }

  const closeRing = (ring: readonly Pt[]): Pt[] => [
    ...ring.map((p): Pt => [p[0], p[1]]),
    [ring[0][0], ring[0][1]] as Pt,
  ];

  const out: PlaceableRegion[] = [];
  for (const region of usableRegions) {
    let result: ReturnType<typeof polygonClipping.difference>;
    try {
      result = polygonClipping.difference(
        [closeRing(region)],
        ...cutters.map((c) => [closeRing(c)] as [Pt[]])
      );
    } catch {
      // The library throws on some pathological inputs. Falling back to the UN-subtracted region
      // would place panels over the keep-out, so the region is dropped instead: fewer panels is
      // recoverable, a panel on a skylight is not.
      continue;
    }

    for (const poly of result) {
      const [outerRing, ...holeRings] = poly;
      if (!outerRing || outerRing.length < 4) continue;
      const outer = toCCW(outerRing.slice(0, -1).map((p): Pt => [p[0], p[1]]));
      if (Math.abs(signedArea(outer)) <= MIN_REGION_AREA_M2) continue;

      const holes: Pt[][] = [];
      for (const ring of holeRings) {
        if (!ring || ring.length < 4) continue;
        const hole = toCCW(ring.slice(0, -1).map((p): Pt => [p[0], p[1]]));
        // A sliver HOLE is kept even below the region threshold: it excludes area rather than
        // offering it, so the conservative direction is to keep it.
        if (hole.length >= 3) holes.push(hole);
      }
      out.push({ outer, holes });
    }
  }
  return out;
}
